import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import moment from 'moment';
import { LeaveStatusButton, LeaveStatusType } from '../../constants';
import { DD_MM_YYYY } from '../../pages/Tasks/TaskConstants';
import { getHHMMSSFromSeconds } from '../../utils/TimeConversionFunctions';
import '../../components/LeaveStatusCard/LeaveStatusCard.css';

const getStatusChipColor = (status) => {
  if (status === LeaveStatusType.APPROVED) return 'success';
  if (status === LeaveStatusType.REJECTED) return 'error';
  return 'warning';
};

const singleLineEllipsisSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const multiLineEllipsisSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  wordBreak: 'break-word',
};

const RemoteOfficeHoursTaskCard = ({
  task,
  variant = 'employee',
  employeeName,
  onApprove,
  onReject,
  onEdit,
  onDelete,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isPending = task.status === LeaveStatusType.PENDING;
  const isApproved = task.status === LeaveStatusType.APPROVED;
  const isRejected = task.status === LeaveStatusType.REJECTED;
  const headerTitle =
    variant === 'manager' ? employeeName || task.employeeId : task.title;
  const showManagerActions =
    variant === 'manager' && (isPending || isApproved) && !isRejected;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <div className="leave-status-container pb-0">
        <div className="space-between-container">
          <Tooltip title={headerTitle || ''} arrow>
            <Typography
              sx={{ width: '30%', minWidth: 0, ...singleLineEllipsisSx }}
              variant="h6"
            >
              {headerTitle}
            </Typography>
          </Tooltip>
          <Typography sx={{ width: '40%', textAlign: 'center' }}>
            <CalendarMonthRoundedIcon fontSize="small" className="mr-1 mt-n1" />
            {moment(task.dateOfRemoteOfficeHours).format(DD_MM_YYYY)}
          </Typography>
          <Box sx={{ width: '30%', textAlign: 'right' }}>
            <Chip
              label={`${getHHMMSSFromSeconds(task.requestedRemoteOfficeHours)} hrs`}
              color="secondary"
              size="small"
              variant="outlined"
              sx={{ marginRight: 1.5 }}
            />
            <Chip
              label={task.status}
              color={getStatusChipColor(task.status)}
              size="small"
              variant="outlined"
            />
          </Box>
        </div>

        {showManagerActions && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
              mt: 2,
              mb: 1,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              color="error"
              sx={{ px: 3 }}
              onClick={() => onReject(task)}
            >
              {isApproved ? LeaveStatusButton.REJECTED : LeaveStatusButton.REJECT}
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="success"
              sx={{ px: 3 }}
              disabled={isApproved}
              onClick={() => onApprove(task)}
            >
              {isApproved ? LeaveStatusButton.APPROVED : LeaveStatusButton.APPROVE}
            </Button>
          </Box>
        )}

        {variant === 'employee' && isPending && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
            <IconButton onClick={() => onEdit(task)}>
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => onDelete(task)}>
              <DeleteIcon />
            </IconButton>
          </Box>
        )}

        <Accordion
          expanded={expanded}
          onChange={() => setExpanded(!expanded)}
          disableGutters
          elevation={0}
          sx={{ width: '100%' }}
        >
          <Grid
            className="leave-container pb-0 pt-0"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              height: '25px',
              alignItems: 'center',
            }}
          >
            <AccordionSummary expandIcon={<KeyboardArrowDownIcon />} />
          </Grid>
          <Grid className="leave-container pb-0 pt-0" sx={{ width: '100%' }}>
            <AccordionDetails className="p-0">
              <Typography variant="body1" className="mt-1 mb-2" color="#4D6CD9">
                <strong>Task Title:</strong>
              </Typography>
              <Tooltip title={task.title || ''} arrow>
                <Typography
                  variant="body1"
                  className="mb-3"
                  sx={singleLineEllipsisSx}
                >
                  {task.title}
                </Typography>
              </Tooltip>
              <Typography variant="body1" className="mt-1 mb-2" color="#4D6CD9">
                <strong>Task Description:</strong>
              </Typography>
              <Tooltip title={task.description || ''} arrow>
                <Typography
                  variant="body1"
                  className="mb-3"
                  sx={multiLineEllipsisSx}
                >
                  {task.description}
                </Typography>
              </Tooltip>
              {task.isAppraisal && (
                <Chip
                  label="Marked for Appraisal"
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
            </AccordionDetails>
          </Grid>
        </Accordion>
      </div>
    </Card>
  );
};

export default RemoteOfficeHoursTaskCard;
